<script>
import { mapState, mapActions } from 'vuex';
import TransitionExpand from '@/components/shared/TransitionExpand.vue';
import prism from 'prismjs';
import 'prismjs/components/prism-markup-templating';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-ruby';
import 'prismjs/components/prism-php';
import 'prismjs/components/prism-java';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-markup';
import 'prismjs/components/prism-swift';

export default {
  components: {
    TransitionExpand,
  },

  props: {
    content: {
      type: Object,
      default: null,
    },

    isApiData: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      snippets: [],
      localLang: '',
      justCopied: false,
    };
  },

  computed: {
    ...mapState('settings', ['darkmode', 'globalLang']),

    currentSnippet() {
      const current = this.snippets.find(
        (snippet) => snippet.fields.language === this.selectedLang,
      );

      return current || '';
    },

    supportedLangs() {
      return this.snippets.map((snippet) => snippet.fields.language);
    },

    supportsCopyToClipboard() {
      return navigator.clipboard;
    },

    selectedLang() {
      const { globalLang, localLang, supportedLangs } = this;
      return supportedLangs.find((lang) => lang === globalLang) ? globalLang : localLang;
    },
  },

  created() {
    this.snippets = this.content.items;
    this.localLang = this.snippets[0].fields.language;
  },

  methods: {
    ...mapActions('settings', ['toggleDarkmode', 'setGlobalLang']),

    selectLangDesktop(lang) {
      this.setGlobalLang(lang);
    },

    formattedSnippet(snippet) {
      const lang = snippet.fields.language;
      const grammar = prism.languages[lang];
      return prism.highlight(snippet.fields.code, grammar, lang);
    },

    selectLangMobile(event) {
      const { value } = event.target;
      this.setGlobalLang(value);
    },

    labelClass(snippet) {
      return { 'is-selected': snippet.fields.language === this.selectedLang };
    },

    copy() {
      this.justCopied = true;

      window.setTimeout(() => {
        this.justCopied = false;
      }, 3000);

      navigator.clipboard.writeText(this.currentSnippet.fields.code);
    },

    showContent(snippet) {
      return { show: snippet.fields.language === this.selectedLang };
    },
  },
};
</script>

<template>
  <div v-if="currentSnippet" class="code-block" :class="{ darkmode }">
    <header class="header">
      <div class="left-wrapper">
        <template v-if="snippets.length > 1">
          <select
            class="label labels-mobile"
            @change="selectLangMobile($event)"
            aria-label="Code languages"
          >
            <option
              v-for="snippet in snippets"
              :key="snippet.sys.id"
              :value="snippet.fields.language"
              :selected="snippet.fields.language === selectedLang"
            >
              {{ snippet.fields.label }}
            </option>
          </select>

          <div class="labels-desktop">
            <button
              v-for="snippet in snippets"
              :key="snippet.sys.id"
              class="label label-button"
              :class="labelClass(snippet)"
              @click="selectLangDesktop(snippet.fields.language)"
            >
              {{ snippet.fields.label }}
            </button>
          </div>
        </template>

        <template v-else>
          <span class="label" :class="{ 'is-selected': !isApiData }">
            {{ snippets[0].fields.label }}
          </span>
        </template>

        <button class="toggle-darkmode-button" aria-label="Toggle darkmode" @click="toggleDarkmode">
          <img class="icon moon" src="@/assets/svg-icons/moon.svg" alt />
          <img class="icon sun" src="@/assets/svg-icons/sun.svg" alt />
        </button>
      </div>

      <div class="copy-wrapper" :class="{ 'just-copied': justCopied }">
        <span class="copied-text animate">Copied</span>

        <button
          v-if="supportsCopyToClipboard"
          class="copy-button"
          aria-label="Copy code"
          @click="copy"
        >
          <span class="icon-wrapper copy">
            <img class="icon" src="@/assets/svg-icons/copy.svg" />
          </span>
          <span class="icon-wrapper copied">
            <img class="icon" src="@/assets/svg-icons/check.svg" />
          </span>
        </button>
      </div>
    </header>

    <div class="code-wrapper">
      <TransitionExpand v-for="snippet in snippets" :key="snippet.sys.id">
        <div class="code-content" v-show="snippet.fields.language === selectedLang">
          <code class="lines fade-in" :class="showContent(snippet)">
            <template v-for="(num, i) in snippet.fields.code.split('\n').length" :key="i">
              <span v-if="snippet.fields.isTerminalCommand">$</span>
              <span v-else>{{ num }}</span>
              <br />
            </template>
          </code>

          <code
            class="prism fade-in"
            :class="showContent(snippet)"
            v-html="formattedSnippet(snippet)"
          />
        </div>
      </TransitionExpand>
    </div>
  </div>
</template>

<style lang="scss" scoped>
$transition: 0.15s linear;
$darkmode-button-width: 36px;
$copy-button-width: 86px;

.code-block {
  margin: $space-24 0;
  box-shadow: 0px 1px 10px rgba(0, 0, 0, 0.15);
  border-radius: $border-radius-xx-small;

  .header {
    border-radius: $border-radius-xx-small $border-radius-xx-small 0 0;
    background: $background-color-secondary;
    height: 40px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    transition: $transition;
    padding-left: $space-8;
  }

  .left-wrapper {
    display: flex;
    align-items: center;
    width: calc(100% - #{$copy-button-width});
  }

  .label {
    @include caption-medium;
    background-color: transparent;
    color: $code-block-text-color-secondary;
    font-weight: $font-weight-bold;
    border: 0;
    padding: $space-8;
    white-space: nowrap;
    overflow-x: hidden;
    text-overflow: ellipsis;

    @include tablet-small {
      max-width: calc(100% - #{$darkmode-button-width});
    }

    &.is-selected {
      color: $interaction-color-primary;
    }

    &::-ms-expand {
      display: none;
    }
  }

  .labels-mobile {
    appearance: none;
    background-position: right 0 center;
    background-image: url('~@/assets/svg-icons/caret-down-blue.svg');
    background-repeat: no-repeat;
    background-color: transparent;
    font-family: $font-family, sans-serif;
    max-width: 7.5rem;
    white-space: nowrap;
    overflow-x: hidden;
    text-overflow: ellipsis;
    padding-right: $space-24;
    //Font size must be 16px at least to avoid zooming in on iPhone, this is a fix.
    font-size: 1rem;
    transform: scale(0.875);
    transform-origin: left;

    &.label {
      color: $interaction-color-primary;
    }

    option {
      // Fix for Mozilla Firefox
      font-weight: normal;
      color: $text-color-primary;
    }

    @include tablet-small {
      display: none;
    }
  }

  .labels-desktop {
    display: none;

    @include tablet-small {
      align-items: center;
      display: flex;
    }
  }

  .label-button {
    height: 100%;
    transition: $transition;

    &.is-selected {
      color: $interaction-color-primary;
    }
  }

  .toggle-darkmode-button {
    display: none;
    margin-left: $space-16;
    border: 0;
    background: #d9d9d9;
    padding: 0;
    width: $darkmode-button-width;
    height: 14px;
    position: relative;
    border-radius: 10px;
    transition: $transition;
    flex-shrink: 0;

    @include tablet-small {
      display: block;
    }

    .icon {
      width: 14px;
      height: 14px;
      transition: $transition;
      background: $background-color-secondary;
      border-radius: 10px;
      padding: 3px;
      box-sizing: content-box;
      position: absolute;
      left: 0;
      top: 50%;
      transform: translate(0%, -50%);
      box-shadow: 0px 0px 1px rgba(0, 0, 0, 0.12), 0px 1px 1px rgba(0, 0, 0, 0.237602);

      &.moon {
        opacity: 0;
      }
    }
  }

  .copy-wrapper {
    display: flex;
    align-items: center;
    width: $copy-button-width;

    .copied-text {
      @include caption-bold;
      color: $interaction-color-primary;
      transition: $transition;
      opacity: 0;
      transform: translateY(100%);
      margin-right: -$space-8;
    }

    .copy-button {
      padding: 0;
      background: none;
      border: 0;
      display: flex;
      flex-wrap: wrap;
      overflow: hidden;
      padding: 0 $space-8;

      .icon-wrapper {
        width: 100%;
        transition: $transition;
        transform: translateY(50%);
      }

      .icon {
        width: 15px;
        height: 15px;
        display: block;
        margin: auto;
      }

      .copied {
        opacity: 0;
      }
    }

    &.just-copied {
      .copied-text {
        opacity: 1;
        transform: translateY(0%);
      }

      .icon-wrapper {
        transform: translateY(-50%);
      }

      .copied {
        opacity: 1;
      }

      .copy {
        opacity: 0;
      }
    }
  }

  .code-wrapper {
    border-radius: 0 0 $border-radius-xx-small $border-radius-xx-small;
    transition: $transition;
  }

  .code-content {
    display: flex;
  }

  .lines {
    width: 44px;
    flex-shrink: 0;
    border-radius: 0 0 0 $border-radius-xx-small;
  }

  code {
    @include caption-medium;
    transition: $transition;
    padding: $space-24 $space-16;
    line-height: 1.5;
    white-space: pre;
  }

  .prism {
    border-radius: 0 0 $border-radius-xx-small 0;

    &::-webkit-scrollbar {
      height: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: $interaction-color-tertiary;
      border-radius: 70px;
    }
  }
}

.fade-in {
  opacity: 0;
  transition: opacity $transition;
  overflow-y: hidden !important;

  &.show {
    opacity: 1;
    animation: 0.4s delay-overflow;
  }
}

// This is so the scrollbar shows up after height animation is finished
@keyframes delay-overflow {
  from {
    overflow-x: hidden;
  }

  to {
    overflow-x: auto;
  }
}

// All dark mode styles here
.darkmode {
  .header {
    background-color: $code-block-background-color-secondary;
  }

  .toggle-darkmode-button {
    background: $code-block-background-color-tertiary;

    .icon {
      transform: translate(15.5px, -50%);
      background: $code-block-button-color-secondary;

      &.sun {
        opacity: 0;
      }

      &.moon {
        opacity: 1;
      }
    }
  }
}

@media print {
  .code-block {
    break-inside: avoid;
    box-shadow: none;

    .toggle-darkmode-button,
    .copy-wrapper {
      display: none;
    }

    // Add some more context to the language label
    .language-button::before {
      content: 'Code example: ';
    }

    // Hide non-displayed language options
    .language-button:not(.is-selected) {
      display: none;
    }

    .code-wrapper {
      // Since lines can break in print, we can't rely
      // on the numbering being correct, so we remove it
      .lines {
        display: none;
      }

      // Physical paper doesn't support side-scrolling,
      // therefore we must break lines in the code examples
      :deep(code *) {
        word-break: break-all !important;
        white-space: pre-wrap;
      }
    }
  }
}
</style>
